import { BaseEntity } from '@/api/EntityApi';
import { DeepKeys } from '@tanstack/react-table';
import { AttributeValue } from '@/api/AttributeValueApi';
import LANGUAGES from '@/configs/languages';

export type Language = keyof typeof LANGUAGES;
export type ForeignLanguage = Exclude<Language, 'PL'>;
export type Translations<TData> = Record<ForeignLanguage, TData>;

export type SortOrder = 'ASC' | 'DESC';

export interface Sort<TData extends BaseEntity = any> {
	key: DeepKeys<TData> | null;
	order: SortOrder;
}

export interface ApiQueryOptions<TData extends BaseEntity> {
	page: number;
	limit: number;
	search: string;
	sortBy: Sort<TData>;
}

export enum FilterOperator {
	EQ = '$eq',
	GT = '$gt',
	GTE = '$gte',
	IN = '$in',
	NULL = '$null',
	LT = '$lt',
	LTE = '$lte',
	BTW = '$btw',
	ILIKE = '$ilike',
	SW = '$sw',
	CONTAINS = '$contains',
}

export type CustomFilters = Record<string, unknown>;

export interface ApiPaginatedResponse<TData extends {} = {}> {
	data: TData[];
	meta: {
		currentPage: number;
		itemsPerPage: number;
		totalItems: number;
		totalPages: number;
	};
}

export interface ApiFormattedResponseData<TData extends {} = {}> {
	data: TData[];
	totalPages: number;
}

export interface CreateManyResponse {
	passed: string[];
	rejected: string[];
}

export interface Attachment {
	id: UUID;
	path: string;
	name: string;
	size: number;
	mimetype: string;
}

export type UUID = ReturnType<typeof crypto.randomUUID>;

export enum RelationType {
	ATTRIBUTE_VALUE = 'ATTRIBUTE_VALUE',
	COMPONENT_VARIATION = 'COMPONENT_VARIATION',
}

export enum RelationAction {
	HIDE = 'HIDE',
	SHOW = 'SHOW',
}

export interface Relation {
	type: RelationType;
	objectId: UUID | '';
	action: RelationAction;
}

export enum CreatorActionType {
	SET_SEGMENT_MAX_WIDTH = 'SET_SEGMENT_MAX_WIDTH',
	SET_SEGMENT_MIN_WIDTH = 'SET_SEGMENT_MIN_WIDTH',
	WITH_MOSQUITO_NET = 'WITH_MOSQUITO_NET',
	WITH_ARMOR = 'WITH_ARMOR',
}

export type CreatorAction = {
	id: UUID;
	attributeValue: AttributeValue;
	type: CreatorActionType;
	payload: CreatorActionPayload;
};

export type CreatorActionPayload = {
	minWidth?: number;
	maxWidth?: number;
	withMosquitoNet?: boolean;
	withArmor?: boolean;
	maxShellsQuantityFormula?: string;
};
